import {
  Container,
  Heading,
  Page,
  TextBalancer,
  Title,
} from 'components/common';
import HeaderSEO from 'components/HeaderSEO';
import SpecialOfferings from 'components/SpecialOfferings';
import { isTruthy } from 'lib/core';
import { getProducts } from 'lib/product/api';
import {
  ProductCategory,
  ProductCategoryGrid,
  SpecialProductCategory,
} from 'lib/productCategory';
import { CollectionMenu } from 'lib/sellingProduct';
import { GetServerSideProps } from 'next';
import { getSession } from 'next-auth/client';

export const getServerSideProps: GetServerSideProps<{
  categories: ProductCategory[];
  collections: CollectionMenu[];
  specialProductCategories: SpecialProductCategory[];
}> = async (ctx) => {
  const session = await getSession(ctx);
  const accessToken = session?.user.accessToken;

  const authHeaders = accessToken
    ? { Authorization: `Bearer ${accessToken}` }
    : undefined;

  const specialProductCategories: SpecialProductCategory[] = process
    .env.SPECIAL_PRODUCT_CATEGORIES
    ? JSON.parse(process.env.SPECIAL_PRODUCT_CATEGORIES)
    : [];

  const specialProductCategoriesWithProducts = (
    await Promise.all(
      specialProductCategories.map(async (category) => {
        const categoryIds = (category.id || '')
          .split(',')
          .map((id) => id.trim())
          .filter(Boolean);

        const { response, error } = await getProducts({
          query: {
            category_ids: categoryIds,
          },
          headers: authHeaders,
        });

        const products = error ? [] : response?.data || [];

        return products.length > 0 ? category : null;
      }),
    )
  ).filter(
    (category): category is SpecialProductCategory =>
      category !== null,
  );

  let collections: CollectionMenu[] = [];

  const newLaunchesCollections =
    process.env.NEW_LAUNCHES_COLLECTION_ID;
  const hotSellerCollections = process.env.HOT_SELLERS_COLLECTION_ID;

  if (newLaunchesCollections)
    collections.push(JSON.parse(newLaunchesCollections));

  if (hotSellerCollections)
    collections.push(JSON.parse(hotSellerCollections));

  const categories: ProductCategory[] = process.env.CATEGORIES
    ? JSON.parse(process.env.CATEGORIES)
    : [];

  const cateringEnabled = isTruthy(process.env.CATERING_ENABLED);
  const ybExpressEnabled = isTruthy(process.env.YB_EXPRESS_ENABLED);

  return {
    props: {
      categories,
      collections,
      specialProductCategories: specialProductCategoriesWithProducts,
      cateringEnabled,
      ybExpressEnabled,
    },
  };
};

const CategoriesPage = ({
  categories,
  collections,
  specialProductCategories,
  cateringEnabled,
  ybExpressEnabled,
}: {
  categories: ProductCategory[];
  collections: CollectionMenu[];
  specialProductCategories: SpecialProductCategory[];
  cateringEnabled: boolean;
  ybExpressEnabled: boolean;
}) => {
  return (
    <Page
      content={
        <Container>
          <HeaderSEO
            title="Yummy Bros - The Most Enjoyable Healthy Meal Prep in Singapore!"
            description="We’ve created healthy renditions of popular Asian cuisines by controlling the ingredients and portions that go into each dish."
            path="https://www.yummybros.com/categories"
            image="https://yb-production.s3.ap-southeast-1.amazonaws.com/images/static/facebook-share-image-2020.jpeg"
            ogType="website"
          />
          <div className="tw-flex tw-flex-col tw-gap-4">
            <Heading className="tw-mt-4">
              <Title className="tw-text-4xl tw-font-bold tw-mb-4 tw-text-center">
                Build Your Perfect Order
              </Title>
              <TextBalancer className="tw-text-center tw-mb-0">
                Add your meals, hit checkout, and pick your delivery
                date. We’ll handle the rest. Boom! You’ve just
                meal-prepped like a pro. No subscriptions, no strings
                attached. We deliver several days a week — order by
                Tuesday or Friday, 6pm.
              </TextBalancer>
            </Heading>
            <ProductCategoryGrid
              categories={categories}
              collections={collections}
              specialProductCategories={specialProductCategories}
            />
            <SpecialOfferings
              title="Also by Yummy Bros"
              isHorizontalScrollOnMobile={false}
              titleClassName="!tw-mb-0 tw-mt-4 lg:!tw-mb-4"
              cateringEnabled={cateringEnabled}
              ybExpressEnabled={ybExpressEnabled}
            />
          </div>
        </Container>
      }
    />
  );
};

export default CategoriesPage;
